import {
  Section,
  SectionContainer,
  SectionProps,
} from "@/components/Sections/Sections";
import { OrganisationLinkButton } from "@/components/OrganisationButton";
import React from "react";
import { LearningOpportunity } from "@/server/backend-api";
import { Assert } from "@/utils/assert";
import { mcn } from "@/utils";
import { LearningOpportunityType } from "@/server/backend-api/generated";

interface Props extends SectionProps {
  learningOpp: LearningOpportunity;
}

export function LearningOppProviderSection(props: Props) {
  const { learningOpp, ...rest } = props;
  const hostingUniversity = learningOpp.hostingUniversity;
  Assert.notNull(hostingUniversity);

  const moduleTypeName =
    learningOpp.type === LearningOpportunityType.Challenge
      ? "Challenge"
      : "Module";

  const hasProviders = learningOpp.challengeProviderOrganisations.length > 0;
  const providerHeader =
    learningOpp.challengeProviderOrganisations.length > 1
      ? `${moduleTypeName} providers`
      : `${moduleTypeName} provider`;

  const hasPartners = learningOpp.associatedPartnerOrganisations.length > 0;
  const partnerHeader =
    learningOpp.associatedPartnerOrganisations.length > 1
      ? "Associated Partners"
      : "Associated Partner";

  function UniversityHeader(props: { classNames: string }) {
    return (
      <div
        className={mcn(
          "col-span-full tablet:col-span-8 tablet-lg:col-span-9",
          props.classNames
        )}
      >
        <p className="text-style-headline5 text-yellow-100">
          Hosting university
        </p>
        <h3 className="text-style-headline3 text-light-yellow">
          {learningOpp.hostingUniversity?.name}
        </h3>
      </div>
    );
  }

  return (
    <Section color={"blue"} {...rest}>
      <SectionContainer grid gridNoYGap>
        <>
          <UniversityHeader classNames="tablet:hidden mb-md" />
          <OrganisationLinkButton
            type="organisation"
            university={hostingUniversity}
            className="col-span-full tablet:col-span-4 tablet-lg:col-span-3 desktop:col-span-3"
          />
          <UniversityHeader classNames="hidden tablet:block" />
        </>

        {hasProviders ? (
          <>
            <h4 className="col-span-full text-style-headline5 mt-8 mb-4">
              {providerHeader}
            </h4>
            {learningOpp.challengeProviderOrganisations.map((provider) => (
              <OrganisationLinkButton
                key={provider.id}
                type="organisation"
                className="col-span-full tablet-sm:col-span-6 tablet:col-span-4 tablet-lg:col-span-3 desktop:col-span-2"
                university={provider}
              />
            ))}
          </>
        ) : null}
        {hasPartners ? (
          <>
            <h3 className="col-span-full text-style-headline5 mt-8 mb-4">
              {partnerHeader}
            </h3>
            {learningOpp.associatedPartnerOrganisations.map((partner) => (
              <OrganisationLinkButton
                key={partner.id}
                type="organisation"
                className="col-span-full tablet-sm:col-span-6 tablet:col-span-4 tablet-lg:col-span-3 desktop:col-span-2"
                university={partner}
              />
            ))}
          </>
        ) : null}
      </SectionContainer>
    </Section>
  );
}
